import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';
import { CriteresEvaluation } from '../CriteresEvaluation';

interface Evaluation {
  id?: string;
  ao_id: string;
  nom: string;
  description: string;
  ordre: number;
  ponderation_globale_pct: number;
}

interface CritereEvaluation {
  id?: string;
  nom: string;
  description: string;
  ponderation_pct: number;
  contenu_html: string;
  ordre: number;
  evaluation_id?: string;
}

interface EvaluationDetailsProps {
  evaluation: Evaluation | null;
  evaluationId: string | null;
  aoId: string;
  criteres: CritereEvaluation[];
  canDelete: boolean;
  onUpdateField: (field: keyof Evaluation, value: string | number) => void;
  onDelete: () => void;
  onCriteresChange: (criteres: CritereEvaluation[]) => void;
}

export const EvaluationDetails: React.FC<EvaluationDetailsProps> = ({
  evaluation,
  evaluationId,
  aoId,
  criteres,
  canDelete,
  onUpdateField,
  onDelete,
  onCriteresChange
}) => {
  const [activeTab, setActiveTab] = React.useState("informations");

  if (!evaluation) {
    return (
      <Card className="h-full flex items-center justify-center">
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground">
            Sélectionnez une évaluation pour voir ses détails
          </p>
        </CardContent>
      </Card>
    );
  }

  const evaluationIndex = evaluation.ordre - 1;

  return (
    <Card className="h-full flex flex-col">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full h-full flex flex-col">
        <div className="border-b px-6 pt-6 flex-shrink-0">
          <TabsList className="w-full grid grid-cols-2">
            <TabsTrigger value="informations">Informations</TabsTrigger>
            <TabsTrigger value="criteres">Critères</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="informations" className="p-6 space-y-4 flex-1 overflow-auto">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nom de l'évaluation *</Label>
              <Input
                value={evaluation.nom}
                onChange={(e) => onUpdateField('nom', e.target.value)}
                placeholder="Ex: Compétences du coordinateur"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={evaluation.description || ''}
                onChange={(e) => onUpdateField('description', e.target.value)}
                placeholder="Description de l'évaluation"
              />
            </div>

            <div className="space-y-2">
              <Label>Pondération globale (%) *</Label>
              <Input
                type="number"
                min="1"
                max="100"
                value={evaluation.ponderation_globale_pct}
                onChange={(e) => onUpdateField('ponderation_globale_pct', parseInt(e.target.value) || 0)}
              />
              <p className="text-xs text-muted-foreground">
                Poids de cette évaluation dans la note finale (doit totaliser 100% avec les autres)
              </p>
            </div>

            <div className="pt-4 border-t">
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={onDelete}
                disabled={!canDelete}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Supprimer cette évaluation
              </Button>
              {!canDelete && (
                <p className="text-xs text-muted-foreground mt-2">
                  Au moins une évaluation est requise
                </p>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="criteres" className="p-6 flex-1 overflow-auto">
          <CriteresEvaluation
            aoId={aoId}
            evaluationId={evaluation.id}
            evaluationName={evaluation.nom || `Évaluation ${evaluationIndex + 1}`}
            initialCriteres={criteres}
            onCriteresChange={onCriteresChange}
          />
        </TabsContent>
      </Tabs>
    </Card>
  );
};
